import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarRange, ExternalLink, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTH_NAMES = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

// Stable, visually distinct color per employee (hash of user_id → fixed palette).
const PALETTE = [
  "#00807E", "#EF4444", "#7C3AED", "#F59E0B", "#2563EB", "#DB2777",
  "#059669", "#EA580C", "#0891B2", "#9333EA", "#65A30D", "#DC2626",
];
const colorForUser = (userId) => {
  let hash = 0;
  for (let i = 0; i < (userId || "").length; i++) hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
};

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

/** Big month-view team calendar — one colored bar per employee per day they're away. */
export default function TeamVacationMonthCalendar({ isAdmin }) {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calStatus, setCalStatus] = useState(null);
  const [settingUp, setSettingUp] = useState(false);

  const gridDays = useMemo(() => {
    const first = startOfMonth(month);
    const firstWeekday = (first.getDay() + 6) % 7; // Monday=0
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - firstWeekday);
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      days.push(d);
    }
    return days;
  }, [month]);

  const load = async () => {
    setLoading(true);
    try {
      const start = isoDate(gridDays[0]);
      const end = isoDate(gridDays[gridDays.length - 1]);
      const r = await api.get("/vacations/team", { params: { start, end } });
      setRows(r.data?.rows?.filter((x) => x.status === "approved") || []);
    } catch (e) { /* silent — non-critical widget */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [month]); // eslint-disable-line

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/calendar/team-calendar/status").then((r) => setCalStatus(r.data)).catch(() => {});
  }, [isAdmin]);

  const setupTeamCalendar = async () => {
    setSettingUp(true);
    try {
      await api.post("/calendar/team-calendar/setup");
      toast.success("Google Team-Kalender eingerichtet und mit dem Team geteilt");
      const r = await api.get("/calendar/team-calendar/status");
      setCalStatus(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSettingUp(false); }
  };

  const entriesByDay = useMemo(() => {
    const map = {};
    for (const r of rows) {
      let cur = new Date(r.start_date + "T00:00:00");
      const end = new Date(r.end_date + "T00:00:00");
      while (cur <= end) {
        const iso = isoDate(cur);
        if (!map[iso]) map[iso] = [];
        map[iso].push(r);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return map;
  }, [rows]);

  const legend = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => { if (!seen.has(r.user_id)) seen.set(r.user_id, r.user_name); });
    return Array.from(seen.entries());
  }, [rows]);

  const todayIso = isoDate(new Date());

  return (
    <div className="border border-slate-200 bg-white" data-testid="vacation-month-calendar">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <CalendarRange size={15} strokeWidth={1.5} className="text-[#00807E]" />
          <div className="font-heading text-lg font-light tracking-tighter text-slate-900" data-testid="vacation-month-label">
            {MONTH_NAMES[month.getMonth()]} {month.getFullYear()}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            data-testid="vacation-month-prev" className="p-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600">
            <ChevronLeft size={14} />
          </button>
          <button onClick={() => setMonth(startOfMonth(new Date()))} data-testid="vacation-month-today" className="text-[10px] font-bold uppercase tracking-overline px-2 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600">
            Heute
          </button>
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            data-testid="vacation-month-next" className="p-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600">
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {isAdmin && (
        <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center justify-between gap-3 flex-wrap text-xs" data-testid="team-calendar-google-panel">
          <div className="text-slate-600">
            <span className="font-bold uppercase tracking-overline text-[10px] text-slate-500 mr-1.5">Google Team-Kalender</span>
            {calStatus?.configured ? (
              <span className="text-emerald-700">Aktiv · genehmigte Anträge werden automatisch eingetragen</span>
            ) : (
              <span className="text-slate-500">Noch nicht eingerichtet — genehmigte Urlaube landen nicht in Google Calendar</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {calStatus?.configured && calStatus?.open_url && (
              <a href={calStatus.open_url} target="_blank" rel="noreferrer" data-testid="team-calendar-open-link"
                className="flex items-center gap-1 text-[#00807E] hover:underline font-medium">
                Im Google Kalender öffnen <ExternalLink size={11} />
              </a>
            )}
            <button onClick={setupTeamCalendar} disabled={settingUp} data-testid="team-calendar-setup-button"
              className="flex items-center gap-1.5 bg-slate-900 text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-overline hover:bg-black disabled:opacity-50">
              {settingUp && <RefreshCw size={11} className="animate-spin" />}
              {calStatus?.configured ? "Team neu synchronisieren" : "Jetzt einrichten"}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-7 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">
        {WEEKDAYS.map((w) => (
          <div key={w} className="p-2 text-center border-r border-slate-100 last:border-r-0">{w}</div>
        ))}
      </div>
      <div className={`grid grid-cols-7 ${loading ? "opacity-50" : ""}`}>
        {gridDays.map((d) => {
          const iso = isoDate(d);
          const inMonth = d.getMonth() === month.getMonth();
          const dayEntries = entriesByDay[iso] || [];
          return (
            <div key={iso} data-testid={`vacation-month-day-${iso}`}
              className={`min-h-[92px] p-1.5 border-r border-b border-slate-100 last:border-r-0 ${inMonth ? "bg-white" : "bg-slate-50/60"} ${iso === todayIso ? "ring-1 ring-inset ring-[#00807E]" : ""}`}>
              <div className={`text-[11px] font-mono mb-1 ${inMonth ? "text-slate-700" : "text-slate-300"}`}>{d.getDate()}</div>
              <div className="space-y-0.5">
                {dayEntries.slice(0, 4).map((r) => (
                  <div key={r.vacation_id} title={`${r.user_name} · ${r.type}`}
                    data-testid={`vacation-month-pill-${r.vacation_id}-${iso}`}
                    className="text-[9px] leading-tight text-white px-1 py-0.5 truncate font-medium"
                    style={{ backgroundColor: colorForUser(r.user_id) }}>
                    {r.user_name?.split(" ")[0]}
                  </div>
                ))}
                {dayEntries.length > 4 && (
                  <div className="text-[9px] text-slate-400">+{dayEntries.length - 4} weitere</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {legend.length > 0 && (
        <div className="p-3 flex items-center gap-3 flex-wrap border-t border-slate-100" data-testid="vacation-month-legend">
          {legend.map(([uid, name]) => (
            <div key={uid} className="flex items-center gap-1.5 text-[11px] text-slate-600" data-testid={`vacation-month-legend-${uid}`}>
              <span className="inline-block w-2.5 h-2.5" style={{ backgroundColor: colorForUser(uid) }} />
              {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
